/**
 * paio-ordering's kitchen change feed, read into the framework's lanes
 * (ORDR-42).
 *
 * `GET /api/v1/ordering/kds/changes/?site_id=&since=&device_id=`:
 * - 200 `{data: {cursor, site_id, brands, tickets, availability, printers, poll_after_ms}}`
 * - 304 when the display is level with the Site;
 * - 404 when the device is not enrolled at that Site or the Site is not the
 *   caller's firm's;
 * - 410 `errors/business/device-revoked` when the device was revoked. Permanent.
 *
 * THE CURSOR ORDERS THE WORLD. Two polls in flight can land in either order,
 * so a 200 whose cursor is not AFTER the one on screen is ignored. Otherwise a
 * slow response would put a bumped ticket back on the rail.
 */

import type { KitchenBrandLane, KitchenBrandTokens, KitchenTicket } from "../framework/types.js";
import { columnFor, isTicketState, type TicketState } from "./ticketStates.js";

export interface FeedTicket {
  readonly ticket: KitchenTicket;
  readonly brandId: string | null;
  readonly serverState: TicketState;
}

export interface FeedBrand {
  readonly brandId: string;
  readonly name: string;
  readonly colour: string;
  readonly connection: KitchenBrandLane["connection"];
  readonly confirmedCursor: string;
  readonly activeCount: number;
}

export interface KdsFeed {
  readonly cursor: number;
  readonly siteId: string;
  readonly brands: readonly FeedBrand[];
  readonly tickets: readonly FeedTicket[];
  readonly pollAfterMs: number;
}

export type FeedOutcome =
  | { readonly kind: "feed"; readonly feed: KdsFeed }
  | { readonly kind: "unchanged" }
  | { readonly kind: "not-enrolled" }
  | { readonly kind: "revoked" }
  | { readonly kind: "failed"; readonly status: number | null; readonly correlationId: string };

export const UNASSIGNED_BRAND_ID = "unassigned";
const DEFAULT_POLL_MS = 5000;

const CONNECTION: Readonly<Record<string, KitchenBrandLane["connection"]>> = {
  LIVE: "live",
  STALE: "stalled",
  UNKNOWN: "connecting",
};

function str(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function modifierText(value: unknown): string | null {
  if (typeof value === "string") {
    return value;
  }
  const record = value as Record<string, unknown> | null;
  return str(record?.["name"]) ?? str(record?.["label"]) ?? null;
}

function parseTicket(raw: unknown): FeedTicket | null {
  const record = raw as Record<string, unknown> | null;
  const id = str(record?.["ticket_id"]);
  const state = record?.["state"];
  if (id === null || !isTicketState(state)) {
    return null;
  }
  const column = columnFor(state);
  if (column === null) {
    return null;
  }
  const placed = Date.parse(str(record?.["placed_at"]) ?? "");
  const lines = Array.isArray(record?.["lines"]) ? (record["lines"] as unknown[]) : [];
  const displayNumber = record?.["display_number"];
  return {
    serverState: state,
    brandId: str(record?.["brand_node_id"]),
    ticket: {
      id,
      reference: displayNumber === null || displayNumber === undefined ? id.slice(0, 8) : String(displayNumber),
      state: column,
      placedAt: Number.isNaN(placed) ? 0 : placed,
      items: lines.map((line, index) => {
        const entry = line as Record<string, unknown> | null;
        const lineId = str(entry?.["ticket_line_id"]) ?? `${id}-${String(index)}`;
        const modifiers = Array.isArray(entry?.["modifiers"])
          ? (entry["modifiers"] as unknown[]).map(modifierText).filter((text): text is string => text !== null)
          : [];
        return {
          id: lineId,
          // The feed names no menu item, so an 86 has nothing to be written
          // against. The route refuses an 86 when no availability seam is wired.
          menuItemId: lineId,
          name: str(entry?.["operational_name"]) ?? "",
          quantity: num(entry?.["quantity"]) ?? 1,
          modifiers,
          bumped: false,
        };
      }),
    },
  };
}

/** The 200 body, or `null` when it is not a feed. */
export function parseFeed(body: unknown): KdsFeed | null {
  const data = (body as { data?: unknown } | null)?.data as Record<string, unknown> | null | undefined;
  const cursor = num(data?.["cursor"]);
  const siteId = str(data?.["site_id"]);
  if (cursor === null || siteId === null || !Array.isArray(data?.["brands"]) || !Array.isArray(data?.["tickets"])) {
    return null;
  }
  const brands: FeedBrand[] = [];
  for (const raw of data["brands"] as unknown[]) {
    const entry = raw as Record<string, unknown> | null;
    const brandId = str(entry?.["brand_node_id"]);
    if (brandId === null) {
      continue;
    }
    brands.push({
      brandId,
      name: str(entry?.["name"]) ?? "",
      colour: str(entry?.["kitchen_colour"]) ?? "",
      connection: CONNECTION[str(entry?.["connection"]) ?? ""] ?? "connecting",
      confirmedCursor: String(entry?.["confirmed_cursor"] ?? ""),
      activeCount: num(entry?.["active_count"]) ?? 0,
    });
  }
  const tickets = (data["tickets"] as unknown[]).map(parseTicket).filter((t): t is FeedTicket => t !== null);
  return { cursor, siteId, brands, tickets, pollAfterMs: num(data?.["poll_after_ms"]) ?? DEFAULT_POLL_MS };
}

/**
 * Fold one response into what is on screen.
 *
 * Returns `current` BY IDENTITY when the response is not after it, so a stale
 * or replayed 200 re-renders nothing.
 */
export function applyFeed(current: KdsFeed | null, incoming: KdsFeed): KdsFeed {
  if (current !== null && incoming.cursor <= current.cursor) {
    return current;
  }
  return incoming;
}

/** Classify a response from the change feed. */
export function outcomeOf(response: { status: number | null; body: unknown; correlationId: string }): FeedOutcome {
  switch (response.status) {
    case 200: {
      const feed = parseFeed(response.body);
      return feed === null
        ? { kind: "failed", status: 200, correlationId: response.correlationId }
        : { kind: "feed", feed };
    }
    case 304:
      return { kind: "unchanged" };
    case 404:
      return { kind: "not-enrolled" };
    case 410:
      return { kind: "revoked" };
    default:
      return { kind: "failed", status: response.status, correlationId: response.correlationId };
  }
}

function tokensFor(colour: string): KitchenBrandTokens {
  const accent = colour === "" ? "var(--color-neutral-700)" : colour;
  return {
    accent,
    onAccent: "var(--color-neutral-50)",
    surface: "var(--color-neutral-50)",
    ink: "var(--color-neutral-900)",
    rail: accent,
  };
}

/**
 * The lanes the board renders.
 *
 * `heardAt` is when the feed last answered at all, a 304 included, so a quiet
 * but healthy lane does not read as stalled. A ticket whose brand is not in
 * `brands[]` goes to an "Unassigned" lane rather than vanishing: a ticket
 * missing from a kitchen screen is a meal nobody cooks.
 */
export function lanesFrom(feed: KdsFeed, heardAt: number): KitchenBrandLane[] {
  const known = new Set(feed.brands.map((brand) => brand.brandId));
  const lanes: KitchenBrandLane[] = feed.brands.map((brand) => ({
    siteId: feed.siteId,
    brandId: brand.brandId,
    brandName: brand.name,
    tokens: tokensFor(brand.colour),
    connection: brand.connection,
    confirmedCursor: brand.confirmedCursor,
    activeCount: brand.activeCount,
    lastEventAt: heardAt,
    tickets: feed.tickets.filter((entry) => entry.brandId === brand.brandId).map((entry) => entry.ticket),
  }));
  const orphans = feed.tickets.filter((entry) => entry.brandId === null || !known.has(entry.brandId));
  if (orphans.length > 0) {
    lanes.push({
      siteId: feed.siteId,
      brandId: UNASSIGNED_BRAND_ID,
      brandName: "Unassigned",
      tokens: tokensFor(""),
      connection: "live",
      confirmedCursor: String(feed.cursor),
      activeCount: orphans.length,
      lastEventAt: heardAt,
      tickets: orphans.map((entry) => entry.ticket),
    });
  }
  return lanes;
}
